// What to say when sign-in or account creation fails.
//
// Firebase hands back an Error whose `message` is sometimes a raw JSON payload
// from the identity endpoint — the sign-in page used to `alert(error)` and put
// that on screen verbatim:
//
//   Error: {"error":{"code":400,"message":"INVALID_LOGIN_CREDENTIALS",
//   "errors":[{"message":"INVALID_LOGIN_CREDENTIALS","domain":"global",...
//
// which tells an admin nothing except that something broke. Every failure now
// resolves to a title and one sentence that says what to do next.
//
// Two lookups, because the same failure arrives labelled two ways: the SDK's
// own `auth/*` code, and — when the SDK does not recognise the response, which
// is exactly what produced the screenshot above — `auth/internal-error` with
// the server's SCREAMING_SNAKE code buried in the message. So the message is
// scanned too.

const WRONG_CREDENTIALS = {
  title: "Those details don't match",
  message:
    "Check the email address and password and try again. If you have forgotten the password, ask an administrator to reset it.",
};

const BY_CODE = {
  // Wrong password, unknown account and the newer combined code all say the
  // same thing on purpose: telling a stranger which half was wrong tells them
  // which addresses are real accounts.
  "auth/invalid-login-credentials": WRONG_CREDENTIALS,
  "auth/invalid-credential": WRONG_CREDENTIALS,
  "auth/wrong-password": WRONG_CREDENTIALS,
  "auth/user-not-found": WRONG_CREDENTIALS,

  "auth/invalid-email": {
    title: "That email address isn't valid",
    message: "It should look like name@arshipper.com.",
  },
  "auth/user-disabled": {
    title: "This account is switched off",
    message:
      "An administrator has disabled it. Ask them to turn it back on before signing in.",
  },
  "auth/too-many-requests": {
    title: "Too many attempts",
    message:
      "Sign-in is paused for a few minutes after several failed tries. Wait, then try again — or ask an administrator to reset the password.",
  },
  "auth/network-request-failed": {
    title: "Can't reach the server",
    message:
      "Check the internet connection and try again. Nothing was submitted.",
  },
  "auth/email-already-in-use": {
    title: "That email already has an account",
    message: "Sign in with it instead, or use a different address.",
  },
  "auth/weak-password": {
    title: "Password is too short",
    message: "Use at least six characters.",
  },
  "auth/operation-not-allowed": {
    title: "Email sign-in is switched off",
    message:
      "Email and password sign-in is disabled on this Firebase project. An administrator has to enable it.",
  },
};

// The server's own codes, as they appear inside an `auth/internal-error`.
const BY_SERVER_CODE = [
  ["INVALID_LOGIN_CREDENTIALS", WRONG_CREDENTIALS],
  ["INVALID_PASSWORD", WRONG_CREDENTIALS],
  ["EMAIL_NOT_FOUND", WRONG_CREDENTIALS],
  ["MISSING_PASSWORD", WRONG_CREDENTIALS],
  ["INVALID_EMAIL", BY_CODE["auth/invalid-email"]],
  ["USER_DISABLED", BY_CODE["auth/user-disabled"]],
  ["TOO_MANY_ATTEMPTS_TRY_LATER", BY_CODE["auth/too-many-requests"]],
  ["EMAIL_EXISTS", BY_CODE["auth/email-already-in-use"]],
  ["WEAK_PASSWORD", BY_CODE["auth/weak-password"]],
  ["OPERATION_NOT_ALLOWED", BY_CODE["auth/operation-not-allowed"]],
];

const FALLBACK = {
  title: "Couldn't sign you in",
  message: "Something went wrong on our side. Please try again in a moment.",
};

export const describeAuthError = (error) => {
  if (!error) return FALLBACK;

  const byCode = BY_CODE[error.code];
  if (byCode) return byCode;

  const text = `${error.code || ""} ${error.message || error}`.toUpperCase();
  const hit = BY_SERVER_CODE.find(([serverCode]) => text.includes(serverCode));
  if (hit) return hit[1];

  return FALLBACK;
};
